import { useEffect, useMemo, useRef, useState } from 'react'
import { normalizeStaffName, parseStaffCsv } from '../lib/csvParser.js'
import { formatMoney } from '../lib/format.js'

// Modal for bulk-importing staff from a roster CSV.
//
// Mirrors the pattern of ItemsImportModal / ScheduleImportModal:
// pick file → parse → preview → confirm. Match strategy is
// case-insensitive name match against existing staff; existing
// rows get partial updates (only null fields filled), new rows
// are inserted with active=true.
//
// Includes a "Download template" link that emits a 4-row CSV with
// the right column headers + a sample row, so a non-technical
// owner has a starting point if they don't already have a sheet.
export default function StaffImportModal({
  open,
  onClose,
  onImport,        // (rows) → Promise<{ inserted, updated, skipped }>
  existingStaff,   // current staff list — for matching preview
}) {
  const fileRef = useRef(null)
  const [filename, setFilename]   = useState('')
  const [parsed, setParsed]       = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]         = useState(null)

  useEffect(() => {
    if (open) {
      setFilename('')
      setParsed(null)
      setError(null)
      setSubmitting(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }, [open])

  // Match preview: which rows are new vs existing in the catalog.
  const matchPreview = useMemo(() => {
    if (!parsed?.rows?.length) return null
    const existingByName = new Map()
    for (const s of existingStaff ?? []) {
      existingByName.set(normalizeStaffName(s.name), s)
    }
    let toInsert = 0
    let toUpdate = 0
    let unchanged = 0
    for (const row of parsed.rows) {
      const key = normalizeStaffName(row.name)
      const existing = existingByName.get(key)
      if (existing) {
        const wouldUpdate =
          ((existing.role == null || existing.role === '') && row.role) ||
          (existing.hourly_wage == null && row.hourly_wage != null) ||
          ((existing.email == null || existing.email === '') && row.email) ||
          ((existing.phone == null || existing.phone === '') && row.phone) ||
          ((existing.notes == null || existing.notes === '') && row.notes)
        if (wouldUpdate) toUpdate++
        else unchanged++
      } else {
        toInsert++
      }
    }
    return { toInsert, toUpdate, unchanged }
  }, [parsed, existingStaff])

  if (!open) return null

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setFilename(file.name)
    setError(null)
    try {
      const text = await file.text()
      const result = parseStaffCsv(text)
      setParsed(result)
      if (result.rows.length === 0) {
        setError(result.errors[0] ?? 'No usable staff rows found in this CSV.')
      }
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[StaffImportModal] parse failed', e2)
      setError('Could not read this file. Make sure it\u2019s a CSV export.')
    }
  }

  async function handleConfirm() {
    if (submitting || !parsed?.rows?.length) return
    setSubmitting(true)
    setError(null)
    try {
      await onImport(parsed.rows)
      onClose?.()
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[StaffImportModal] import failed', e2)
      setError('Could not save to the database. Try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // Trigger a CSV download of a starter template the manager can
  // open in Sheets/Excel, fill in, and re-upload. Generated client-
  // side so it always reflects the current parser's expected columns.
  function handleDownloadTemplate() {
    const csv = [
      'Name,Role,Hourly Wage,Email,Phone,Notes',
      'Jamie Lopez,line cook,18.00,jamie@example.com,(555) 123-4567,Available weekends',
      'Sam Chen,server,16.50,sam@example.com,(555) 987-6543,',
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'staff-template.csv'
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Import staff"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">Import staff</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 text-sm leading-relaxed text-blue-900/90">
            <div className="mb-1 font-medium">CSV columns the parser looks for:</div>
            <div className="text-[13px]">
              <span className="font-medium">Name (first + last, required)</span>,
              Role, Hourly Wage, Email, Phone, Notes.
            </div>
            <div className="mt-1 text-xs text-blue-900/70">
              First and last name are required so two staff who share a first
              name don't get merged into one record. Rows with just a first
              name are skipped — fix those in your sheet and re-upload.
            </div>
            <div className="mt-2 text-xs text-blue-900/70">
              Don't have a CSV?{' '}
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="font-medium text-blue-700 underline hover:text-blue-800"
              >
                Download a starter template
              </button>
              , fill it in with your team, save as CSV, and upload here.
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="staff-file">
              Choose CSV file
            </label>
            <input
              ref={fileRef}
              id="staff-file"
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              className="block w-full rounded-lg border border-black/15 px-3 py-3 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700"
            />
            {filename && (
              <p className="mt-1 truncate text-xs text-black/55">
                Selected: <span className="font-medium text-black/80">{filename}</span>
              </p>
            )}
          </div>

          {/* Detected columns */}
          {parsed?.headers?.length > 0 && (
            <div className="rounded-xl border border-black/10 bg-black/[0.02] p-3 text-xs">
              <div className="mb-1 font-medium text-black/60">
                Detected columns
              </div>
              <div className="space-y-0.5">
                <DetectedRow label="Name"        idx={parsed.columns?.nameIdx}  headers={parsed.headers} />
                <DetectedRow label="Role"        idx={parsed.columns?.roleIdx}  headers={parsed.headers} optional />
                <DetectedRow label="Hourly wage" idx={parsed.columns?.wageIdx}  headers={parsed.headers} optional />
                <DetectedRow label="Email"       idx={parsed.columns?.emailIdx} headers={parsed.headers} optional />
                <DetectedRow label="Phone"       idx={parsed.columns?.phoneIdx} headers={parsed.headers} optional />
                <DetectedRow label="Notes"       idx={parsed.columns?.notesIdx} headers={parsed.headers} optional />
              </div>
            </div>
          )}

          {/* Match preview */}
          {matchPreview && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
              <div className="mb-1 text-xs font-medium text-emerald-900/70">
                Preview
              </div>
              <div className="text-sm text-emerald-900">
                <span className="font-medium tabular-nums">{matchPreview.toInsert}</span>{' '}
                new {matchPreview.toInsert === 1 ? 'person' : 'people'} will be added,{' '}
                <span className="font-medium tabular-nums">{matchPreview.toUpdate}</span>{' '}
                existing {matchPreview.toUpdate === 1 ? 'person' : 'people'} will be updated
                {matchPreview.unchanged > 0 && (
                  <>
                    , <span className="font-medium tabular-nums">{matchPreview.unchanged}</span>{' '}
                    already up to date
                  </>
                )}.
              </div>

              {parsed?.rows?.length > 0 && (
                <details className="mt-3 border-t border-emerald-200/60 pt-2">
                  <summary className="cursor-pointer text-[11px] font-medium text-emerald-900/60">
                    Preview rows
                  </summary>
                  <ul className="mt-1 max-h-48 overflow-y-auto text-xs text-emerald-900/85">
                    {parsed.rows.slice(0, 30).map((r, i) => (
                      <li key={i} className="flex items-baseline justify-between gap-2 border-b border-emerald-200/40 py-1 last:border-b-0">
                        <span className="truncate font-medium">{r.name}</span>
                        <span className="tabular-nums text-right text-[11px]">
                          {r.role ? r.role : ''}
                          {r.hourly_wage != null ? ` \u00b7 ${formatMoney(r.hourly_wage)}/hr` : ''}
                        </span>
                      </li>
                    ))}
                    {parsed.rows.length > 30 && (
                      <li className="py-1 text-[11px] text-emerald-900/60">
                        {'\u2026'} and {parsed.rows.length - 30} more
                      </li>
                    )}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* Per-row warnings */}
          {parsed?.errors?.length > 0 && parsed.rows.length > 0 && (
            <details className="rounded-xl border border-amber-200 bg-amber-50/40 p-3 text-xs text-amber-900">
              <summary className="cursor-pointer font-medium">
                {parsed.errors.length} row{parsed.errors.length === 1 ? '' : 's'} skipped during parsing
              </summary>
              <ul className="mt-2 space-y-1">
                {parsed.errors.slice(0, 20).map((msg, idx) => (
                  <li key={idx} className="leading-snug">{msg}</li>
                ))}
              </ul>
            </details>
          )}

          {!parsed && !error && (
            <p className="text-xs text-black/55">
              Existing staff are matched by name (case-insensitive). The import
              fills in missing role, wage, email, phone, or notes on existing
              records but never overwrites your edits. New names are added as
              active staff.
            </p>
          )}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || !parsed?.rows?.length}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? 'Importing\u2026'
              : parsed?.rows?.length
                ? `Import ${parsed.rows.length} ${parsed.rows.length === 1 ? 'person' : 'people'}`
                : 'Pick a CSV first'}
          </button>
        </div>
      </div>
    </div>
  )
}

function DetectedRow({ label, idx, headers, optional }) {
  const found = idx != null && idx >= 0
  return (
    <div className="flex justify-between gap-2">
      <span className="text-black/60">{label}{optional ? ' (optional)' : ''}</span>
      <span className={`truncate font-medium ${found ? 'text-emerald-700' : (optional ? 'text-black/40' : 'text-red-600')}`}>
        {found ? headers[idx] : (optional ? '\u2014' : 'not found')}
      </span>
    </div>
  )
}
